import { Injectable } from '@angular/core';
import { CardDefinition, Order, VisibilityMode } from '@barthauer/abc/cards-view';
import { CollectionHelper, ConvertHelper, ObjectHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  FilterDocumentsModel,
  Query,
  QueryObjectsModel,
  QueryParameter,
  ReadDocumentModel,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { from, Observable } from 'rxjs';
import {
  BCEDocument,
  BCEElement,
  BCEFolder,
  BCEGroup,
  BCEGroupPacket,
  BCEObject,
  BCETemplate,
} from '../models/bce.model';
import { DocumentCard, FolderCard, GroupCard, TemplateCard } from '../viewModels/bce.viewModel';

enum BCETypeEnum {
  Groups,
  Folders,
  Documents,
  Templates,
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private _hierarchies: Map<BCETypeEnum, Order>;
  private async getHierarchies(): Promise<Map<BCETypeEnum, Order>> {
    if (this._hierarchies === undefined) {
      const res = await this.getResourceSet();
      this._hierarchies = new Map<BCETypeEnum, Order>();
      this._hierarchies.set(
        BCETypeEnum.Groups,
        new Order(BCETypeEnum.Groups, res.get('Groups'), VisibilityMode.Default, res.svgName('Group-Image'))
      );
      this._hierarchies.set(
        BCETypeEnum.Folders,
        new Order(BCETypeEnum.Folders, res.get('Folders'), VisibilityMode.Default, res.svgName('Folder-Image'))
      );
      this._hierarchies.set(
        BCETypeEnum.Documents,
        new Order(BCETypeEnum.Documents, res.get('Documents'), VisibilityMode.Default, res.svgName('Document-Image'))
      );
      this._hierarchies.set(
        BCETypeEnum.Templates,
        new Order(BCETypeEnum.Templates, res.get('Templates'), VisibilityMode.Full, res.svgName('Template-Image'))
      );
    }
    return this._hierarchies;
  }

  constructor(private dataService: CoreDataService, private resourceService: ResourceManagerService) {}

  public async getResourceSet(): Promise<ResourceSet> {
    return await this.resourceService.getResourceSet('abc-management-configuration-explorer');
  }

  public getGroupsCardData(
    groupPacket?: BCEGroupPacket[],
    folderId?: string,
    xPathFilter?: string,
    allowTemplates?: boolean
  ): Observable<GroupCard[]> {
    return from(this.getGroupsCardDataCore(groupPacket, folderId, xPathFilter, allowTemplates));
  }

  public async getGroupsCardDataCore(
    groupPacket: BCEGroupPacket[],
    folderId: string,
    xPathFilter: string,
    allowTemplates?: boolean
  ): Promise<GroupCard[]> {
    const groups = await this.queryData(groupPacket, folderId);

    await this.filterGroupsByXPath(groups, xPathFilter);

    return this.createViewModel(await this.getResourceSet(), groups, allowTemplates);
  }

  private async filterGroupsByXPath(groups: BCEGroup[], xPathFilter: string): Promise<void> {
    if (!xPathFilter) {
      return;
    }
    const map: Map<string, BCEElement> = new Map();
    this.processElements(groups, (el) => map.set(el.Id, el));
    const idsToFilter = await this.queryFilterDocuments(ObjectHelper.asArray(map.keys()), xPathFilter);
    for (const id of idsToFilter) {
      map.delete(id);
    }
    this.processElements(groups, (el, parent, index) => {
      if (!(el instanceof BCEFolder) && map.get(el.Id)) {
        delete parent.Childs[index];
      }
    });
    this.processElements(groups, (el) => {
      if (el instanceof BCEFolder && el.Childs?.length) {
        el.Childs = el.Childs.filter((_) => _);
      }
    });

    let removed: boolean;
    do {
      removed = false;
      this.processElements(groups, (el, parent, index) => {
        if (el instanceof BCEFolder && !el.Childs?.length) {
          delete parent.Childs[index];
          removed = true;
        }
      });
      if (removed) {
        this.processElements(groups, (el) => {
          if (el instanceof BCEFolder && el.Childs?.length) {
            el.Childs = el.Childs.filter((_) => _);
          }
        });
      }
    } while (removed);

    CollectionHelper.removeByFilter(groups, (g) => !g.Childs?.filter((el) => el)?.length);
  }

  private processElements(objects: BCEObject[], func: (el: BCEElement, parent?: BCEObject, index?: number) => void) {
    for (const obj of objects) {
      if (obj?.Childs) {
        for (let i = 0; i < obj.Childs.length; i++) {
          const el = obj.Childs[i];
          func(el, obj, i);
          this.processElements([el], func);
        }
      }
    }
  }

  private createViewModel(res: ResourceSet, groups: BCEGroup[], allowTemplates?: boolean): GroupCard[] {
    const groupsCards: GroupCard[] = [];
    groups?.forEach((element) => {
      const groupCard: GroupCard = this.createViewModelCore(res, element, allowTemplates) as GroupCard;
      if (groupCard) {
        groupsCards.push(groupCard);
      }
    });
    return groupsCards;
  }

  public async queryFilterDocuments(ids: string[], xpath: string): Promise<string[]> {
    const queryCard: FilterDocumentsModel = new FilterDocumentsModel();
    queryCard.OutputMode = 0;
    queryCard.Ids = ids;
    queryCard.XPath = xpath;
    const data = ConvertHelper.arrayBufferToObjectArray(
      await this.dataService.executeFilterDocumentsQuery(queryCard).toPromise()
    );
    const result: string[] = [];
    for (const row of data) {
      const id = row['id'];
      if (id) {
        result.push(id);
      }
    }
    return result;
  }

  private async queryData(groupPacket: BCEGroupPacket[], folderId?: string): Promise<BCEGroup[]> {
    const queryCard: QueryObjectsModel = new QueryObjectsModel();
    queryCard.ObjectSpaceName = 'Master';
    queryCard.OutputMode = 0;
    queryCard.Parameters = [new QueryParameter('groupPacket', groupPacket)];
    const cardListObjectsQuery: Query = new Query();
    const cardContainerObjectsQuery: Query = new Query();
    cardListObjectsQuery.ObjectType = 'MGroupItem';
    if (groupPacket) {
      cardListObjectsQuery.OPath = 'Id In ($groupPacket)';
    }
    cardListObjectsQuery.Columns = [
      this.dataService.createQueryColumn('Designation', 'Designation'),
      this.dataService.createQueryColumn('Id', 'Id'),
      this.dataService.createQueryColumn('Description', 'Description'),
    ];
    cardContainerObjectsQuery.Name = 'Nodes';
    if (folderId) {
      cardContainerObjectsQuery.OPath = `Elements[Id='${folderId}']`;
    } else {
      cardContainerObjectsQuery.OPath = 'Elements';
    }

    cardContainerObjectsQuery.Columns = [
      this.dataService.createQueryColumn('Designation', 'Designation'),
      this.dataService.createQueryColumn('Id', 'Id'),
      this.dataService.createQueryColumn('ActiveX', 'ActiveX'),
      this.dataService.createQueryColumn('IdRefMElementParent', 'IdRefMElementParent'),
      this.dataService.createQueryColumn('Description', 'Description'),
      this.dataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate'),
      this.dataService.createQueryColumn('SysUserNameUpdate', 'SysUserNameUpdate'),
      this.dataService.createQueryColumn(
        "IIF(Type==0, 'Folder', IIF(Type==1, 'Document',IIF(Type==2, 'Template')))",
        'Type'
      ),
    ];
    cardListObjectsQuery.ObjectQueries = [cardContainerObjectsQuery];
    queryCard.ObjectQueries = [cardListObjectsQuery];

    const data = ConvertHelper.arrayBufferToObjectArray(
      await this.dataService.executeReadObjectsQuery(queryCard).toPromise()
    );

    const result: BCEGroup[] = [];
    for (const row of data) {
      result.push(new BCEGroup(row));
    }
    return result;
  }

  public queryDocument(id): Observable<any> {
    const query: ReadDocumentModel = new ReadDocumentModel();
    query.Id = id;
    return this.dataService.executeReadDocumentQuery(query);
  }

  public createViewModelCore(res: ResourceSet, bceObject: BCEObject, allowTemplates: boolean): CardDefinition {
    let cardDefinition: CardDefinition;

    if (bceObject instanceof BCEGroup) {
      cardDefinition = new GroupCard(res, bceObject, (_) => this.createViewModelCore(res, _, allowTemplates));
      this.getHierarchies().then((_) => (cardDefinition.Order = _.get(BCETypeEnum.Groups)));
    } else if (bceObject instanceof BCEFolder) {
      cardDefinition = new FolderCard(res, bceObject, (_) => this.createViewModelCore(res, _, allowTemplates));
      if (cardDefinition.CardDefinitions?.length) {
        this.getHierarchies().then((_) => (cardDefinition.Order = _.get(BCETypeEnum.Folders)));
      } else {
        cardDefinition = undefined;
      }
    } else if (bceObject instanceof BCEDocument) {
      cardDefinition = new DocumentCard(bceObject);
      this.getHierarchies().then((_) => (cardDefinition.Order = _.get(BCETypeEnum.Documents)));
    } else if (allowTemplates && bceObject instanceof BCETemplate) {
      cardDefinition = new TemplateCard(bceObject);
      this.getHierarchies().then((_) => {
        let order = _.get(BCETypeEnum.Templates);
        if (allowTemplates && order) {
          order = new Order(order.Number, order.DisplayText, VisibilityMode.Default);
        }
        cardDefinition.Order = order;
      });
    }

    return cardDefinition;
  }

  public static getDocumentCount(cardDefinition: CardDefinition): number {
    let count: number = 0;

    cardDefinition.CardDefinitions?.forEach((cd) => {
      if (cd instanceof DocumentCard) {
        count++;
      } else if (cd instanceof FolderCard) {
        count += this.getDocumentCount(cd);
      }
    });
    return count;
  }
}
